import axios from "axios";
import { type ExternalLink, type ExternalLinkFormData } from "../types/externalLinkTypes";
import { type TagsToContent } from "interfaces";
import { type DistributedOperationResult } from "types";
import { type TagsOptions, type Publisher } from "features/Library/EmbeddedVideos/services/types";
import { type ContentResponse } from "features/Application/services/dataServices/contentDataService";
import { type Filters, serializeArrayAndFilterNullable } from "utils/queryUtils";
import { type SortingDirection } from "enums";
import { type LinkUser } from "../types/models";
import { type PeopleGroupAssignment } from "features/Library/PeopleAssignments/Overview/helpers/getColumnOptions";

const BASE_URL = "/api/assets/links";

export const externalLinksDataService = {
  async getExternalLinkById(id: number): Promise<ExternalLink> {
    const response = await axios.get(`${BASE_URL}/${id}`);
    return response.data;
  },

  createExternalLink(payload: ExternalLinkFormData) {
    return axios.post<number>(BASE_URL, { payload: JSON.stringify(payload) });
  },

  getPublishedExternalLinksByIdsAsync(ids: string[], showPurchased: boolean) {
    return axios.get<ContentResponse[]>(`${BASE_URL}/published`, {
      params: { ids, showPurchased },
      paramsSerializer: serializeArrayAndFilterNullable,
    });
  },

  updateExternalLink(id: number, payload: ExternalLinkFormData): Promise<void> {
    return axios.put(`${BASE_URL}/${id}`, payload);
  },

  async getExternalLinkTags(): Promise<{ labels: string[]; softwareApplications: string[] }> {
    const response = await axios.get(`${BASE_URL}/tags`);
    return response.data;
  },

  getTagsFilterOptions(showPurchased: boolean) {
    return axios.get<TagsOptions>(`${BASE_URL}/tags`, { params: { showPurchased } });
  },

  addTagsToExternalLink(data: TagsToContent) {
    return axios.post<{ messageIds: string[] }>(`${BASE_URL}/tags`, data);
  },

  // Returns operation id
  async updateExternalLinkTags(id: number, payload: TagsToContent): Promise<string> {
    const response = await axios.put(`${BASE_URL}/${id}/tags`, payload);
    return response.data;
  },

  deleteExternalLinks(ids: number[]) {
    return axios.delete<DistributedOperationResult>(`${BASE_URL}`, { data: { ids } });
  },

  changeExternalLinkVisibility(ids: number[], visibility: boolean) {
    return axios.post<DistributedOperationResult>(`${BASE_URL}/visibility`, { ids, visibility });
  },

  duplicateExternalLinks(ids: number[]) {
    return axios.post<DistributedOperationResult>(`${BASE_URL}/duplicate`, { ids });
  },

  getPublishersFilterOptions() {
    return axios.get<Publisher[]>(`${BASE_URL}/publishers`);
  },

  getExternalLinkUsers(
    linkId: number,
    skip: number,
    top: number,
    sortBy: string,
    sortOrder: SortingDirection,
    filters: Filters,
    term?: string,
  ) {
    return axios.get<LinkUser[]>(`/api/v4/content/link/${linkId}/users`, {
      params: {
        skip,
        top,
        sortBy,
        sortOrder,
        ...filters,
        term,
      },
      paramsSerializer: serializeArrayAndFilterNullable,
    });
  },

  getExternalLinkGroups(
    linkId: number,
    skip: number,
    top: number,
    sortBy: string,
    sortOrder: SortingDirection,
    filters: Filters,
    term?: string,
  ) {
    return axios.get<PeopleGroupAssignment[]>(`/api/v4/content/link/${linkId}/groups`, {
      params: {
        skip,
        top,
        sortBy,
        sortOrder,
        ...filters,
        term,
      },
      paramsSerializer: serializeArrayAndFilterNullable,
    });
  },
} as const;
